import { mutate } from 'swr';

import { DiagramChatChannel, DiagramChatChannelCollaborator, DiagramChatChannelCompany } from 'models';
import { ChatHelpers } from 'helpers/ChatHelpers';

import { PayloadSDK } from 'services/PayloadSDK';

type ChannelReference = Pick<DiagramChatChannel, 'id' | 'project'>;

type AccessCollection = 'diagramChatChannelCollaborators' | 'diagramChatChannelCompanies';
type AccessRow = DiagramChatChannelCollaborator | DiagramChatChannelCompany;

const list = async <T extends AccessRow>(collection: AccessCollection, projectId: DiagramChatChannel['project']) => {
    const { docs } = await PayloadSDK.find({
        collection,
        where: { project: { equals: projectId } },
        sort: 'createdAt',
        depth: 0,
        pagination: false,
    });

    return docs as unknown as T[];
};

const add = async (collection: AccessCollection, channel: ChannelReference, member: Record<string, unknown>) => {
    await PayloadSDK.create({
        collection,
        data: { project: channel.project, channel: channel.id, ...member },
    } as Parameters<typeof PayloadSDK.create>[0]);

    await ChatChannelAccessService.refresh(channel.project);
};

const remove = async (collection: AccessCollection, row: AccessRow) => {
    await PayloadSDK.delete({ collection, id: row.id });

    await ChatChannelAccessService.refresh(row.project);
};

/**
 * Who has access to the chat channels of a project on top of the project team. Users and companies
 * are rows of their own collections, which are read and changed directly.
 */
const ChatChannelAccessService = {
    listCollaborators: (projectId: DiagramChatChannel['project']) => {
        return list<DiagramChatChannelCollaborator>('diagramChatChannelCollaborators', projectId);
    },

    listCompanies: (projectId: DiagramChatChannel['project']) => {
        return list<DiagramChatChannelCompany>('diagramChatChannelCompanies', projectId);
    },

    addUser: (channel: ChannelReference, userId: DiagramChatChannelCollaborator['user']) => {
        return add('diagramChatChannelCollaborators', channel, { user: userId });
    },

    removeCollaborator: (collaborator: DiagramChatChannelCollaborator) => {
        return remove('diagramChatChannelCollaborators', collaborator);
    },

    addCompany: (channel: ChannelReference, companyId: DiagramChatChannelCompany['company']) => {
        return add('diagramChatChannelCompanies', channel, { company: companyId });
    },

    removeCompany: (company: DiagramChatChannelCompany) => {
        return remove('diagramChatChannelCompanies', company);
    },

    refresh: async (projectId: DiagramChatChannel['project']) => {
        await Promise.all([
            mutate(ChatHelpers.swr.channelCollaborators(projectId)),
            mutate(ChatHelpers.swr.channelCompanies(projectId)),
        ]);
    },
};

export { ChatChannelAccessService };
